import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { toast, TOAST } from '../../utils/toastify';

import { addToCart } from '../../pages/api';
import { updateCart } from '../../store/modules/cart/actions';

// Utils
import { aa } from '../../utils/algolia';
import { logAmplitude, amplitudeActive } from '../../utils/amplitude';
import { formatSplitTests } from '../../utils/amplitudeHelpers/splitTestHelpers';
import getCookie from '../../utils/getCookie';
import { trackAddToCart, trackLineItemChange, LUX } from '../../utils/tracking';

// Context
import { useProduct } from '../../utils/context/product-provider';
import { useSearch } from '../../utils/context/search-provider';

// Components
import MonogramForm from '../monogram-form';
import Button from '../../atoms/button';
import Select from '../../atoms/select';
import Typography from '../../atoms/typography';
import WaitlistEmail from '../../molecules/product-waitlist-email';

const SubmitButton = styled(Button, { shouldForwardProp: (prop) => prop !== 'displayOpacity' })`
  font-family: ${(props) => props.theme.font.caption};
  font-size: ${(props) => props.theme.modularScale.sixteen};  
  outline: 0;
  padding: 0 1rem;
  max-height: 4.8rem;
  width: 100%;
  text-transform: uppercase;
  letter-spacing: .2rem;


  ${({ displayOpacity }) => (displayOpacity && css`
  :hover {
    opacity: 1;
  }
  `)}
`;

const ButtonWrapper = styled('span', { shouldForwardProp: (prop) => prop !== 'hasVariants' })`
  display: flex;
  flex-direction: center;
  width: 100%;
  height: 100%;

  ${({ hasVariants }) => hasVariants && css`
    width: 70%;
  `}
`;

const SizeSelect = styled(Select, { shouldForwardProp: (prop) => prop !== 'showWarningMsg' })`
  color: ${(props) => props.theme.color.brand};
  outline: 0;
  width: 25%;
  height: 100%;
  font-size: ${(props) => props.theme.modularScale.sixteen};
  border: 0.1rem solid ${({ showWarningMsg, theme }) => (showWarningMsg ? theme.color.brandA11yRed : theme.color.borderBlue)};

  ${(props) => (
    props.theme.filterArrow('down', props.theme.color.brand, 'right 1px right', 7)
  )}
  `;

const BagCountWrapper = styled.div`
  position: absolute;
`;

const BagCountNotification = styled.span`
  height: 0;
  width: 0;
  opacity: 0;
`;

const WarningText = styled(Typography)`
  font-family: ${(props) => props.theme.font.sans};
  color: ${(props) => props.theme.color.brandA11yRed};
  font-size: ${({ theme }) => theme.modularScale.sixteen};
  display: block;
  line-height: 1.5;
`;

const WarningWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 0.2rem 1rem 0.2rem;
`;

const CallToActionWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 4.8rem;
  `;
  
const FILTERED_STICKYMOBILE_EMO_PROPS = new Set(['notAvailable', 'showWarningMsg']);

const MobileProductAdd = styled('div', {
  shouldForwardProp: (prop) => !FILTERED_STICKYMOBILE_EMO_PROPS.has(prop)
})`
  // TODO: the original height values (15% : 10%) seemed to be causing issues
  // with changing grid component heights when toggling the description accordion.
  // this should be refactored to set auto, unless we needed those values.
  // seems to be fine without them.
  height: ${({ showWarningMsg }) => (showWarningMsg ? 'auto' : 'auto')};
  width: 100%;
  display: ${({ notAvailable }) => (notAvailable ? 'none' : 'flex')};
  flex-direction: column;
  justify-content: ${(showWarningMsg) => (showWarningMsg ? 'space-evenly' : 'space-around')};
  background-color: white;
  margin-bottom: 3rem;
`;

const MobilePaidProductAdd = (props) => {
  const {
    state: {
      addMonogram,
      monogramColor,
      monogramFont,
      monogramText,
      warning
    },
    setAddMonogram,
    handleMonogramOnly,
    setProductAdded,
    setProductAddedModalActive,
    updateWarningMessage
  } = useProduct();

  const { state: { queryID, objectIDs } } = useSearch();

  const getOptionType = () => props.product?.option_types?.[0]?.name || 'Size';
  const hasVariants = (() => {
    if (!props.product?.variants || props.product?.variants.length === 1) return false;
    return props.product?.variants.every((v) => v.option_values.length > 0);
  })();

  const [addingToCart, setAddingToCart] = useState(false);
  const [variant, setVariant] = useState(null);
  const [option, setOption] = useState('0');
  const [optionOutOfStock, setOptionOutOfStock] = useState(false);
  const [optionType] = useState(getOptionType());
  const [outOfStock, setOutOfStock] = useState(false);
  const [quantity] = useState('1');
  const [renderBagNotification, setRenderBagNotification] = useState(false);

  useEffect(() => {
    if (props.product?.variants?.length === 1) {
      const handleSingleVariantReset = (singleVariantArray) => {
        const [v] = singleVariantArray;
        const o = v.option_values[0].name;
    
        props.onVariantChange(v);
        setVariant(v);
    
        setAddMonogram(v?.prices?.[0]?.monogram?.monogrammable_only ?? false);
        setOption(o);
        setOutOfStock(v.total_on_hand === 0);
      };

      handleSingleVariantReset(props.product.variants);
    }

    if (props.product?.variants?.every((v) => v.in_stock === false)) {
      const handleOnOutOfStock = () => {
        setOutOfStock(true);
      };

      handleOnOutOfStock();
    }

    updateWarningMessage('');
  }, []);

  const reachedStockLimit = (selectedVariant) => {
    if (selectedVariant && selectedVariant.total_on_hand > 0) {
      const itemQuantity = props.cart?.line_items?.
        find((x) => x.variant_id === selectedVariant?.id)?.quantity || 0;

      const priceObj = props?.vendor
        ? selectedVariant.prices.find((x) => x.vendor_id === props.vendor)
        : selectedVariant?.prices[0];

      const max = 10;

      const count = priceObj.total_on_hand - itemQuantity > max
        ? max : priceObj.total_on_hand - itemQuantity;

      return count <= 0;
    }

    return false;
  };

  const handleAddToCart = async ({
    count,
    userId,
    variantId,
    vendorId
  }) => {
    const { monogram_customizations } = props.variant.prices[0].monogram;

    if (props.product.variants.length > 1) {
      setOption(null);
      props.onVariantChange(null);
    }
    
    setAddingToCart(true);

    if (addMonogram) {
      updateWarningMessage('');
    }

    const userToken = getCookie('maisonette_user_token');
    const orderToken = getCookie('maisonette_order_token');
    const sessionToken = getCookie('maisonette_session_token');
    const splitTests = getCookie('maisonette_split_tests');

    const request = {
      line_item: {
        quantity: count,
        variant_id: variantId,
        vendor_id: vendorId
      },
      // add browser state to track missing orders and the changes is temporary
      browser: {
        amplitude_active: amplitudeActive,
        ga_active: !!(global.window.ga && global?.ga?.q),
        maisonette_session_token: sessionToken,
        ab_tests: splitTests ? formatSplitTests(JSON.parse(splitTests)) : false
      },
      channel : 'Web'
    };

    if (addMonogram) {
      request.line_item.monogram_attributes = {};
      request.line_item.monogram_attributes.text = monogramText;

      if (monogram_customizations?.colors?.length > 0) {
        request
          .line_item
          .monogram_attributes
          .customization = request.line_item.monogram_attributes.customization || {};

        request.line_item.monogram_attributes.customization.color = monogram_customizations.colors
          .find((color) => color.value === monogramColor.hex);
      }

      if (monogram_customizations?.fonts?.length > 0) {
        request
          .line_item
          .monogram_attributes
          .customization = request.line_item.monogram_attributes.customization || {};

        request.line_item.monogram_attributes.customization.font = monogram_customizations.fonts
          .find((font) => font.value === monogramFont);
      }
    }

    if (userToken && userId) request.user_id = userId;
    if (orderToken !== undefined && orderToken !== 'undefined') request.order_token = orderToken;

    LUX.addedToCart();

    const abortController = new global.window.AbortController();

    await addToCart({ body: request, signal: abortController.signal })
      .then((res) => {
        // if the user adds an item to the cart and quickly tries to navigate away
        // the request will abort and continue with the .then block.
        // aborting the request will prevent this from happening
        if (Object.keys(res).length === 0) abortController.abort();
        if (abortController.signal.aborted) return;

        if (res?.errors && Array.isArray(res.errors)) {
          res.errors.forEach(({ message = null }) => {
            if (typeof message === 'string') {
              toast(message, { type: TOAST.TYPE.ERROR });
            }
          });

          setAddingToCart(false);
          return;
        }

        props.updateCart(res);

        global.document.cookie = `maisonette_order_token=${res.token}; max-age=15768017; path=/;`;
        global.document.cookie = `maisonette_order_number=${res.number}; max-age=15768017; path=/;`;

        LUX.cartValue(res);
        LUX.cartSize(res);

        const trackVariant = props?.product?.variants?.find((v) => v.id === variantId);

        trackAddToCart({
          product: props.product,
          cart: res,
          quantity: count,
          variant: trackVariant,
          user: props.user
        });

        logAmplitude('Added To Cart', {
          product: props.product,
          cart: res,
          quantity: count,
          variant: trackVariant,
          position: props.position,
          referer: document.referrer ? document.referrer : props.meta?.referer,
          outlet: props.outlet
        });

        // if the user opened PDP from direct link
        // or from Algolia PLP in a new tab,
        // then queryID is null and objectIDs is an empty array
        // because the user was not using an Algolia search.
        // Therefore we cannot persist the necessary data to send to Algolia
        if (queryID && objectIDs.length) {
          if (aa) {
            // aa is needed to send event
            // when user adds to cart on PDP
            aa('convertedObjectIDsAfterSearch',
              {
                eventName: 'Added to Cart',
                index: process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX,
                queryID,
                objectIDs
              });
          }
        }

        setTimeout(() => {
          setAddingToCart(false);
          setRenderBagNotification(true);
        }, 1500);

        if (props.isMobileDevice) {
          const { line_item: { variant_id } } = request;
          setProductAdded(variant_id);
          setProductAddedModalActive(true);
          logAmplitude('Add to Cart Confirmation', {
            product: props.product,
            isViewed: true
          });
        }

        setTimeout(() => {
          // remove notification to trigger alert for screen readers
          // if another item is added
          setRenderBagNotification(false);
        }, 2000);
      });
  };

  const canSubmit = () => {
    if (props.cart.loading) return false;

    let isMonogramValid = true;

    if (variant?.prices?.length && addMonogram) {
      const { monogram_customizations = {} } = variant.prices[0].monogram;

      let isColorValid = true;
      let isFontValid = true;

      if (Object.prototype.hasOwnProperty.call(monogram_customizations, 'colors')) {
        isColorValid = monogram_customizations.colors.length === 0 || monogramColor;
      }

      if (Object.prototype.hasOwnProperty.call(monogram_customizations, 'fonts')) {
        isFontValid = monogram_customizations.fonts.length === 0 || monogramFont;
      }

      const isTextValid = monogramText;

      isMonogramValid = !!isColorValid && !!isFontValid && !!isTextValid;
    }

    if (!isMonogramValid && addMonogram) return 'Please provide all monogram options';
    if (outOfStock || optionOutOfStock) return 'OOS';
    if (variant === null || option === null) return `Please select a ${optionType.toLowerCase()}`;

    return true;
  };

  const handleOnSelectChange = (e) => {
    const { variants } = props.product;

    setOption(e.currentTarget.value);

    if (e.currentTarget.value.includes('(SOLD OUT)')) {
      setOptionOutOfStock(true);
    } else {
      setOptionOutOfStock(false);
    }

    // update the variant with the user-selected variant
    let newVariant;
    if (e.currentTarget.value.includes('(')) {
      const trimmedValue = e.currentTarget.value.split('(')[0].trim();
      newVariant = variants.find((v) => v.option_values[0].name === trimmedValue);
    } else {
      newVariant = variants.find((v) => v.option_values[0].name === e.currentTarget.value);
    }

    if (reachedStockLimit(newVariant)) {
      updateWarningMessage('No more left');
    } else if (warning.includes('monogram') !== true) {
      updateWarningMessage('');
    }

    props.onVariantChange(newVariant);
    setVariant(newVariant);

    const monogram = newVariant?.prices[0]?.monogram;
    if (monogram?.monogrammable_only) handleMonogramOnly();

    if (!e.currentTarget.value.includes('(SOLD OUT)')) {
      trackLineItemChange({
        product: props.product,
        quantity,
        variant: newVariant
      });
    }
  };

  const renderSizeOptions = () => {
    if (hasVariants) {
      const { variants } = props.product;
      const options = [];

      variants.forEach((v) => {
        let oosText = '';

        if (v.total_on_hand === 1) oosText = '(Only 1 Left!)';
        if (v.total_on_hand === 0) oosText = '(SOLD OUT)';

        options.push(<option key={v.option_values[0].name.trim()}>{`${v.option_values[0].name} ${oosText}`}</option>);
      });

      return options;
    }

    return [];
  };

  const handleOnSubmit = (e) => {
    e.preventDefault();

    const validSubmission = canSubmit();

    if (validSubmission === true) {
      setAddingToCart(true);
      handleAddToCart({
        variantId: props.variant.id,
        vendorId: props.vendor,
        count: quantity,
        userId: props.userId
      });
    } else if (validSubmission.includes('Please')) {
      updateWarningMessage(validSubmission);
    }
  };

  const buttonDisabled = () => (addingToCart
    || props.cart.loading
    || reachedStockLimit(props.variant)
    || warning === 'No more left')
    || outOfStock
    || optionOutOfStock;

  return (
    <>
    <MobileProductAdd
      data-vwo-id="MobilePaidMobileProductAdd"
      showWarningMsg={warning.length > 0 || reachedStockLimit(props.variant)}
      notAvailable={!props.product.available || props.product.discontinued}
    >
      {
        props.variant?.in_stock
        && props.variant?.prices[0]?.monogram?.monogrammable && (
          <MonogramForm
            setMonogramSelected={props.setMonogramSelected}
            variant={props.variant}
          />
        )
      }

      {
        warning.length || reachedStockLimit(props.variant) ? (
          <WarningWrapper data-vwo-id="MobilePaidProductWarningWrapper">
            <WarningText element="p" like="label-1">
              {reachedStockLimit(props.variant) ? 'No more left' : warning}
            </WarningText>
          </WarningWrapper>
        ) : null
      }

      <CallToActionWrapper>
        {hasVariants && !outOfStock ? (
          <>
            <SizeSelect
              data-vwo-id="MobilePaidProductSizeSelect"
              name="size"
              id="sticky-product-add-to-cart-size-select"
              value={!option ? '0' : option.trim()}
              onChange={handleOnSelectChange}
              outline
              showWarningMsg={(warning.length > 0 && !warning.includes('monogram')) || reachedStockLimit(props.variant)}
            >
              <option value="0" disabled hidden>{`${optionType}`}</option>
              {renderSizeOptions()}
            </SizeSelect>
          </>
        ) : null}
        <ButtonWrapper
          hasVariants={hasVariants && !outOfStock}
        >
          <SubmitButton
            data-vwo-id="MobilePaidProductSubmitButton"
            type="submit"
            data-test-id="add_to_cart"
            id="sticky-product-add-to-cart-button"
            disabled={buttonDisabled()}
            onClick={handleOnSubmit}
            displayOpacity={!buttonDisabled()}
          >
            <>
             Add to bag
            </>
          </SubmitButton>
        </ButtonWrapper>
      </CallToActionWrapper>
      <WaitlistEmail
          active={outOfStock || optionOutOfStock}
          variant={variant?.id ?? null}
        />
    </MobileProductAdd>
    {
        // below renders a bag update message for screen readers only
        // not visible on screen
        renderBagNotification && (
          <BagCountWrapper data-vwo-id="MobilePaidProductBagCountWrapper">
            <BagCountNotification
              role="alert"
              aria-live="polite"
            >
              {`Added to Bag. Bag now has ${props.cart.count} items`}
            </BagCountNotification>
          </BagCountWrapper>
        )
      }
    </>
  );
};

MobilePaidProductAdd.defaultProps = {
  cart: {},
  isMobileDevice: false,
  position: null,
  variant: null,
  vendor: null,
  updateCart: () => { },
  user: null,
  userId: null,
  meta: {}
};

MobilePaidProductAdd.propTypes = {
  cart: PropTypes.object,
  isMobileDevice: PropTypes.bool,
  onVariantChange: PropTypes.func.isRequired,
  position: PropTypes.number,
  product: PropTypes.object.isRequired,
  setMonogramSelected: PropTypes.func.isRequired,
  variant: PropTypes.object,
  vendor: PropTypes.number,
  updateCart: PropTypes.func,
  user: PropTypes.object,
  userId: PropTypes.number,
  meta: PropTypes.object
};

const mapStateToProps = (state) => ({
  cart: state.cart,
  isMobileDevice: state.interfaces.isMobileDevice,
  user: state.profile,
  userId: state.profile.id
});

const mapDispatchToProps = (dispatch) => ({
  updateCart: (cart) => dispatch(updateCart(cart))
});

export default connect(mapStateToProps, mapDispatchToProps)(MobilePaidProductAdd);
